import { ChangeDetectionStrategy, Component, computed, effect, inject, input, InputSignal, QueryList, ViewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GoogleMapsModule } from '@angular/google-maps';
import { RouteDataService } from '../../services/route-data.service';
import { Point, ItemStatus } from 'src/app/pages/custom-route/interfaces/custom-route.interface';
import { DEFAULT_MARKER, SATURATED_MARKER, RECOMMENDED_MARKER } from './config/constants';
import { MapService } from '../../services/map.service';

@Component({
    selector: 'fitur-map-detail-page-markers',
    standalone: true,
    imports: [ 
        CommonModule, 
        GoogleMapsModule,
    ],
    templateUrl: './detail-page-marker.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})

export class FiturMapDetailPageMarkerComponent {
    public map: InputSignal<any> = input<any>();
    public detailPoint: InputSignal<any> = input<any>(undefined);
    private _routeDataService: RouteDataService = inject(RouteDataService);
    private _zoomIntervalId: ReturnType<typeof setInterval> | null = null;
    public routeSegments = computed<Point[]>(() =>this._routeDataService.getCurrentRouteDay())

    public routeItem = computed(() => {
        return this.routeSegments()?.find(e => e.id === this.detailPoint()?.id);
    })

    public routeItemIndex = computed(() => {
        return this.routeSegments()?.findIndex(e => e.id === this.detailPoint()?.id);
    })

    e = effect(() => {
        const location = this.routeItem()?.currentLocation;
        if (!location)
            return;

        const suggestedItems = this.routeItem()?.relatedsItems?.map?.(point => point.currentLocation) ?? undefined;
        const coords = [location, suggestedItems]?.flat()?.filter(e => e !== undefined);
        if (coords.length > 1){
            this._fitBounds(coords)
            return;
        }
        
        this._zoomToCoordinates(location);
    })


    // Markers --------------------------------------------------------------------------------------------------------------------
    public defaultMakerOption = (index) => DEFAULT_MARKER(index + 1);
    public saturatedMakerOption = (index) => SATURATED_MARKER(index + 1);
    public recommendedMarkerOption = () => RECOMMENDED_MARKER();


    public handleRecommendedMarkerClick(item: Point): void{
        console.log("handleRecommendedMarkerClick", item)
        if (!item?.id)
            return;
    }

    public getItemOptions(item){
        return item.relateds
            ? this.saturatedMakerOption(this.routeItemIndex())
            : this.defaultMakerOption(this.routeItemIndex())
    }

    private _fitBounds(coords){
        setTimeout(() => {
            const bounds = new google.maps.LatLngBounds();
            coords?.forEach(coord => bounds.extend(new google.maps.LatLng(coord)));
            this.map()?.googleMap?.fitBounds(bounds);
        }, 200);

    }

    private _zoomToCoordinates(coords: { lat: number; lng: number }): void {
        if (!this.map()?.googleMap) 
            return;
        
        const targetZoom = 18; // Zoom final deseado
        const currentZoom = this.map().googleMap.getZoom() || 10;
        
        // Primero centrar en las coordenadas
        this.map().googleMap.panTo(coords);
        
        // Animar el zoom gradualmente
        this._animateZoom(currentZoom, targetZoom);
    }

    private _animateZoom(currentZoom: number, targetZoom: number): void {
        if (!this.map()?.googleMap) 
            return;
        
        // Limpiar animación anterior si existe
        if (this._zoomIntervalId) {
            clearInterval(this._zoomIntervalId);
        }
        
        const step = currentZoom < targetZoom ? 0.5 : -0.5;
        const duration = 50;
        
        this._zoomIntervalId = setInterval(() => {
            currentZoom += step;
            
            if ((step > 0 && currentZoom >= targetZoom) || (step < 0 && currentZoom <= targetZoom)) {
                this.map()?.googleMap?.setZoom(targetZoom);
                clearInterval(this._zoomIntervalId!);
                this._zoomIntervalId = null;
            } else {
                this.map()?.googleMap?.setZoom(currentZoom);
            }
        }, duration);
    }

    ngOnDestroy(): void {
        console.log("Destroying DetailPageMarker!")
        if (this._zoomIntervalId) {
            clearInterval(this._zoomIntervalId);
            this._zoomIntervalId = null;
        }
    }
    
}